import { FC } from 'react';
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  FormControl,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  JiraTicket,
  JiraProject,
  Nullable,
  JiraIssueType,
  IssueTypeField,
} from 'common-types';
import { MdSearch as SearchIcon } from 'react-icons/md';
import CreateJiraTicketFieldsGridWrapper from './CreateJiraTicketFieldsGridWrapper';

const CreateJiraTicketBaseFields: FC<CreateJiraTicketBaseFieldsProps> = ({
  jiraTicketFields,
  projects,
  onJiraTicketFormChange,
  onInputChange,
  onProjectChange,
  onIssueTypeChange,
  issueTypes,
  loadingProjects,
  loadingDependentByProjectFields,
  fieldsMetadata,
  validationObject,
}) => {
  const { project, issueType } = validationObject || {};

  const { summary } = validationObject || {};
  return (
    <>
      <Tooltip
        placement='left'
        arrow
        title={
          jiraTicketFields.issueType &&
          fieldsMetadata &&
          !fieldsMetadata['summary']
            ? `The 'Summary' field is not supported for the issue type '${jiraTicketFields.issueType.name}' in project '${jiraTicketFields.project?.key}'`
            : ''
        }>
        <div>
          <FormControl size='small' fullWidth>
            <TextField
              error={!!summary}
              helperText={!!summary ? summary.message : ''}
              required={
                fieldsMetadata?.['summary']?.required &&
                !fieldsMetadata?.['summary']?.hasDefaultValue
              }
              disabled={!!(fieldsMetadata && !fieldsMetadata['summary'])}
              multiline
              label='Summary'
              maxRows={2}
              size='small'
              defaultValue={jiraTicketFields.summary}
              onChange={onJiraTicketFormChange}
              name='summary'
            />
          </FormControl>
        </div>
      </Tooltip>
      <CreateJiraTicketFieldsGridWrapper>
        <div>
          <FormControl required size='small' fullWidth>
            <Autocomplete
              noOptionsText='Search Project Key'
              aria-required
              size='small'
              loading={loadingProjects}
              filterOptions={x => x}
              options={projects || []}
              autoComplete
              isOptionEqualToValue={(option, value) => {
                return option.key === value.key;
              }}
              value={jiraTicketFields.project}
              onChange={onProjectChange}
              getOptionLabel={option => option.key}
              onInputChange={onInputChange}
              popupIcon={<SearchIcon />}
              renderInput={params => (
                <TextField
                  {...params}
                  error={!!project}
                  helperText={!!project ? project.message : ''}
                  required
                  placeholder='Project Key'
                  size='small'
                  hiddenLabel
                  color='primary'
                  name='project'
                  label='Project'
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Typography color='text.primary' variant='body2'>
                    {option.key}
                  </Typography>
                </li>
              )}
            />
          </FormControl>
        </div>
        <Tooltip
          placement='right'
          arrow
          title={!!!jiraTicketFields.project ? 'Please select project' : ''}>
          <div>
            <FormControl required size='small' fullWidth>
              <Autocomplete
                aria-required
                size='small'
                loading={loadingDependentByProjectFields}
                options={issueTypes || []}
                disabled={!!!jiraTicketFields.project}
                isOptionEqualToValue={(option, value) => {
                  return option.name === value.name;
                }}
                value={jiraTicketFields.issueType}
                onChange={onIssueTypeChange}
                getOptionLabel={option => option.name || ''}
                renderInput={params => (
                  <TextField
                    {...params}
                    error={!!issueType}
                    helperText={!!issueType ? issueType.message : ''}
                    required
                    placeholder='Issue Type'
                    size='small'
                    hiddenLabel
                    color='primary'
                    name='issuetype'
                    label='Issue Type'
                    fullWidth
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Typography color='text.primary' variant='body2'>
                      {option.name}
                    </Typography>
                  </li>
                )}
              />
            </FormControl>
          </div>
        </Tooltip>
      </CreateJiraTicketFieldsGridWrapper>
    </>
  );
};

export interface CreateJiraTicketBaseFieldsProps {
  jiraTicketFields: JiraTicket;
  projects: Nullable<JiraProject[]>;
  issueTypes: Nullable<JiraIssueType[]>;
  loadingProjects: boolean;
  loadingDependentByProjectFields: boolean;
  fieldsMetadata: Nullable<{
    [key: string]: IssueTypeField;
  }>;
  validationObject: Nullable<{
    [key: string]: {
      message: string;
    };
  }>;
  onIssueTypeChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: Nullable<Partial<JiraIssueType>>,
    reason: AutocompleteChangeReason,
  ) => void;
  onProjectChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: Nullable<JiraProject>,
    reason: AutocompleteChangeReason,
  ) => void;
  onInputChange: (
    event: React.SyntheticEvent<Element, Event>,
    value: Nullable<string>,
    reason: AutocompleteInputChangeReason,
  ) => void;
  onJiraTicketFormChange: (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>,
  ) => void;
}

export default CreateJiraTicketBaseFields;
